import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Box,
  TextField,
  Button,
  Dialog,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../config";

const LocationandInterests = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  const [enteredCity, setEnteredCity] = useState("");
  const [interests, setInterests] = useState({
    jobs: false,
    services: false,
    offers: false,
    events: false,
    classifieds: false,
  });

  useEffect(() => {
    if (!user?.id) {
      console.warn("❌ User ID not available in context");
      return;
    }

    const fetchUserDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}`);
        const data = response.data; // ✅ Fix: use root-level object
        console.log("✅ Fetched user data:", data);

        setUserData(data);
        setEnteredCity(data.interested_city || "");
        setInterests({
          jobs: data.show_jobs || false,
          services: data.show_services || false,
          offers: data.show_offers || false,
          events: data.show_events || false,
          classifieds: data.show_classifieds || false,
        });
      } catch (error) {
        console.error("🚨 Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [user]);

  const handleOpenDialog = () => setOpen(true);
  const handleCancel = () => {
    localStorage.setItem("hasSeenDialog", "true");
    setOpen(false);
  };

  const handleInterestChange = (event) => {
    setInterests({
      ...interests,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSave = async () => {
    const payload = {
      interested_city: enteredCity,
      show_jobs: interests.jobs,
      show_services: interests.services,
      show_offers: interests.offers,
      show_events: interests.events,
      show_classifieds: interests.classifieds,
    };

    try {
      if (user?.id) {
        await axios.put(`${API_BASE_URL}/users/${user.id}`, payload);
        setOpen(false);
        const updated = await axios.get(`${API_BASE_URL}/users/${user.id}`);
        setUserData(updated.data); // ✅ Fix: use flat data
      } else {
        console.error("❌ User ID is missing!");
      }
    } catch (error) {
      console.error("🔥 Error saving preferences:", error);
      alert("Failed to save preferences.");
    }
  };

  if (loading || !userData) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Typography variant="h4" fontWeight="bold" textAlign="center" mb={3} color="#42326e">
        My Location and Interests
      </Typography>

      <Card sx={{ boxShadow: "1px 1px 2px 2px rgba(94, 238, 207, 0.6)" }}>
        <CardHeader
          title={userData.name || "No Name"}
          titleTypographyProps={{ fontWeight: "bold" }}
          subheader={`Joined on ${new Date(userData.created_at).toLocaleDateString()}`}
        />
        <CardContent>
          <Box>
            <Typography ><strong>City:</strong> {userData?.interested_city || "Not provided"}</Typography>
            <Typography><strong>Interested Jobs:</strong> {userData.show_jobs ? "Yes" : "No"}</Typography>
            <Typography><strong>Interested Services:</strong> {userData.show_services ? "Yes" : "No"}</Typography>
            <Typography><strong>Interested in Offers:</strong> {userData.show_offers ? "Yes" : "No"}</Typography>
            <Typography><strong>Interested in Events:</strong> {userData.show_events ? "Yes" : "No"}</Typography>
            <Typography><strong>Interested in Classifieds:</strong> {userData.show_classifieds ? "Yes" : "No"}</Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={handleOpenDialog}
              sx={{ backgroundColor: "#0653a2", color: "white", padding: "12px 15px", borderRadius: 2 }}
            >
              Update
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Update Dialog */}
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Enter Your City</Typography>
          <TextField
            fullWidth
            label="City Name"
            variant="outlined"
            value={enteredCity}
            onChange={(e) => setEnteredCity(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" gutterBottom>You are interested in:</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            {["jobs", "services", "events", "offers", "classifieds"].map((item) => (
              <FormControlLabel
                key={item}
                control={
                  <Checkbox
                    checked={interests[item]}
                    onChange={handleInterestChange}
                    name={item}
                  />
                }
                label={item.charAt(0).toUpperCase() + item.slice(1)}
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCancel}  sx={{ backgroundColor: "red", color: "white", padding: "8px 12px", borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}  sx={{ backgroundColor: "#0653a2", color: "white", padding: "8px 12px", borderRadius: 2 }}>Save</Button>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
};

export default LocationandInterests;
